import clsx from 'clsx'
import {useFormik} from 'formik'
import React, {useState} from 'react'
import {useIntl} from 'react-intl'
import {CSSTransition} from 'react-transition-group'
import * as Yup from 'yup'
import SettingsService from '../../../../core/_requests'
import {Button, ButtonBack, InputTextFormik} from '../../../../../../../_gori/partials/widgets'
import {StoresListing} from '../StoresListing'
import UseYupValidate from '../../../../../../../_gori/hooks/UseYupValidate'
import {OrderType} from '../../../../../orders/core/_const'
import useCancelToken from '../../../../../../../_gori/hooks/UseCancelToken'

import {PromotionBanner, BannerContent} from '../../../../../common/components/PromotionBanner'
import {usePromoEligibility} from '../../../../../../../_gori/hooks/UsePromoEligibility'
import {ConnectionLink} from '../ConnectionLink'
import {KTSVG} from '../../../../../../../_gori/helpers'
import {useNavigate} from 'react-router-dom'
import useWindowDimensions from '../../../../../../../_gori/hooks/useWindowDimensions'
import {TABLET_BREAKPOINT} from '../../../../../../../_gori/constants'

const ConnectShopify: React.FC = () => {
  const intl = useIntl()
  const {regexYup} = UseYupValidate()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const {getMarketPromoStatus} = usePromoEligibility()
  const shopifyPromoStatus = getMarketPromoStatus('shopify')
  const isPromoEligible = shopifyPromoStatus.isEligible
  const isPromoApplied = shopifyPromoStatus.isApplied
  const {isCancel} = useCancelToken()
  const navigate = useNavigate()
  const {width} = useWindowDimensions()

  const validationSchema = Yup.object().shape({
    storeLink: regexYup.storeLink,
  })

  const initialValues = {
    storeLink: '',
  }

  const onSubmit = async (values, {setStatus}) => {
    setIsLoading(true)
    await SettingsService.getShopifyIntegrateLink(values.storeLink)
      .then((res) => {
        window.location.replace(res)
      })
      .catch((error) => {
        if (isCancel(error)) return
        setStatus(error?.response?.data?.error?.message ?? '')
      })
      .finally(() => setIsLoading(false))
  }

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  })

  const shopifyBannerContent: Partial<BannerContent> = {
    helpLinkTo: '/help-center/store-connection/shopify',
  }

  return (
    <>
      <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
        <div className='store-connect-page'>
          <div className='store-connect-page__header'>
            <div className='store-connect-page__header-main'>
              <div className='d-flex align-items-center store-connect-page__back'>
                <ButtonBack className='store-connect-page__back-button me-0' />
                <button
                  type='button'
                  className='store-connect-page__back-arrow'
                  aria-label='Go back'
                  onClick={() => navigate(-1)}
                >
                  <KTSVG path='/media/gori/left_arrow.svg' />
                </button>
              </div>
              <div className='d-flex flex-column'>
                <div className='d-flex gap-4 align-items-center flex-wrap'>
                  <span className='store-connect-page__title fw-bolder text-dark fs-3'>
                    {intl.formatMessage({id: 'CONNECT_TO'})} Shopify
                  </span>
                </div>
                <ConnectionLink url={shopifyBannerContent.helpLinkTo || ''} />
              </div>
            </div>
            {isPromoApplied && (
              <PromotionBanner
                isEligible={isPromoEligible}
                isApplied={isPromoApplied}
                content={shopifyBannerContent}
              />
            )}
          </div>

          <div className='store-connect-page__body'>
            <div className='store-connect-page__form-card'>
              <div className='d-flex justify-content-center row mb-0'>
                <div
                  className={clsx('d-flex align-items-start col-12 store-connect-page__form-row', {
                    'flex-column': width < TABLET_BREAKPOINT,
                  })}
                >
                  <div
                    className={clsx(`d-flex store-connect-page__form-field`, {
                      'col-8': width >= TABLET_BREAKPOINT,
                      'col-12 align-items-start flex-column': width < TABLET_BREAKPOINT,
                    })}
                  >
                    <InputTextFormik
                      className={clsx('col-10', {'col-12': width < TABLET_BREAKPOINT})}
                      labelClassName='store-connect-page__form-field-label col-2 col-form-label text-nowrap'
                      required
                      label={'Shopify ' + intl.formatMessage({id: 'STORE_DOMAIN'})}
                      formik={formik}
                      name={'storeLink'}
                    />
                  </div>
                  <div
                    className={clsx('btn-group store-connect-page__connect-btn', {
                      'cursor-no-drop': isLoading,
                      'w-100': width < TABLET_BREAKPOINT,
                    })}
                  >
                    <Button
                      classNameDiv={clsx({
                        'w-100': width < TABLET_BREAKPOINT,
                      })}
                      className={clsx('btn btn-advanced-primary w-100', {
                        'pe-none': isLoading,
                        'cursor-no-drop': isLoading,
                      })}
                      loading={isLoading}
                      loadingText={intl.formatMessage({id: 'CONNECT'})}
                      label={intl.formatMessage({id: 'CONNECT'})}
                      event={() => {
                        formik.submitForm()
                      }}
                    />
                  </div>
                </div>
                {formik.status && (
                  <div className='d-flex align-items-start col-12'>
                    <div className='col-9 d-flex align-items-center'>
                      <div className='col-md-3'></div>
                      <div className='col-md-9 mt-5 alert alert-danger'>
                        <div className='alert-text font-weight-bold'>{formik.status}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className='store-connect-page__stores'>
              <StoresListing storeType={OrderType.SHOPIFY} />
            </div>
          </div>
        </div>
      </CSSTransition>
    </>
  )
}

export {ConnectShopify}
